import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useTripDaysServiceV1GetTripDaysThresholdQuery,
  useTripDaysServiceV1UpdateTripDaysThresholdMutation,
  type TripDaysServiceV1UpdateTripDaysThresholdBody,
} from "_services/modules/trip_days";
import { DurationCondition } from "_services/modules/tripDaysThreshold";
import { yupNumTransform } from "_helper/formatters";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

export interface EditTripDaysThresholdFormValues {
  tripDaysName: string;
  durationCondition: DurationCondition;
  durationValue: number | string;
  durationMin: number | string;
  durationMax: number | string;
  equivalentDays: number | string;
  description: string;
}

const schema = yup.object({
  tripDaysName: yup.string().required("Trip Days Name is required").max(100, "Max 100 characters"),
  durationCondition: yup.string().oneOf(["below", "between", "above"]).required("Duration Condition is required"),
  durationValue: yup.mixed().when("durationCondition", {
    is: (val: string) => val === "below" || val === "above",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Duration value is required").positive("Must be positive"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMin: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Min value is required").min(0, "Must be >= 0"),
    otherwise: () => yup.mixed().notRequired(),
  }),
  durationMax: yup.mixed().when("durationCondition", {
    is: "between",
    then: () => yup.number().transform(yupNumTransform).typeError("Must be a number").required("Max value is required").positive("Must be positive")
      .when("durationMin", (durationMin, schema) => {
        const minVal = Array.isArray(durationMin) ? durationMin[0] : durationMin;
        if (typeof minVal === "number" && !isNaN(minVal)) {
          return schema.moreThan(minVal, "Max must be greater than Min");
        }
        return schema;
      }),
    otherwise: () => yup.mixed().notRequired(),
  }),
  equivalentDays: yup.number().transform(yupNumTransform).typeError("Must be a number").required("Equivalent Days is required").positive("Must be positive"),
  description: yup.string().max(500, "Max 500 characters").default(""),
}).required();

const useEditTripDaysThresholdForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("tripDaysThreshold");

  const { data: detail } = useTripDaysServiceV1GetTripDaysThresholdQuery(
    { id: id || "" },
    { skip: !id }
  );
  const [updateTripDaysThreshold] = useTripDaysServiceV1UpdateTripDaysThresholdMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    control,
    watch,
    setValue,
    setError,
  } = useForm<EditTripDaysThresholdFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema) as never,
    defaultValues: {
      // TODO: load from API using GET /trip-days-thresholds/{id}
      tripDaysName: "Trip Days 3",
      durationCondition: "above",
      durationValue: 18,
      durationMin: "",
      durationMax: "",
      equivalentDays: 2,
      description: "",
    },
  });

  const durationCondition = watch("durationCondition");

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditTripDaysThresholdFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  useEffect(() => {
    if (detail) {
      const data = detail;
      setValue("tripDaysName", data.trip_days_threshold_name || "");
      setValue("durationCondition", (data.duration_condition || "below") as any);
      setValue("equivalentDays", data.equivalent_days ?? "");
      
      if (data.duration_condition === "between") {
        setValue("durationMin", data.duration_min ?? "");
        setValue("durationMax", data.duration_max ?? "");
      } else if (data.duration_condition === "below") {
        setValue("durationValue", data.duration_max ?? "");
      } else if (data.duration_condition === "above") {
        setValue("durationValue", data.duration_min ?? "");
      }
    }
  }, [detail, setValue]);

  const onSubmit = (data: EditTripDaysThresholdFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      const payload: Omit<TripDaysServiceV1UpdateTripDaysThresholdBody, 'status'> & { status?: string } = {
        trip_days_threshold_name: pendingData.tripDaysName,
        duration_condition: pendingData.durationCondition,
        duration_min: pendingData.durationCondition === "between"
          ? Number(pendingData.durationMin)
          : (pendingData.durationCondition === "above" ? Number(pendingData.durationValue) : undefined),
        duration_max: pendingData.durationCondition === "between"
          ? Number(pendingData.durationMax)
          : (pendingData.durationCondition === "below" ? Number(pendingData.durationValue) : undefined),
        equivalent_days: Number(pendingData.equivalentDays),
      };
      await updateTripDaysThreshold({ id, tripDaysServiceV1UpdateTripDaysThresholdBody: payload }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("tripDaysName", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
      }
      setShowSaveModal(false);
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    control,
    durationCondition,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    t,
  };
};

export default useEditTripDaysThresholdForm;
